/**
 * The option scaffolding every chart family shares: surface, ink, legend,
 * tooltip, and the accessibility layer.
 *
 * `EChartsOption` is imported as a type only — that emits no runtime import, so
 * this module stays free of the echarts runtime. Only `ChartSurface` imports it
 * for real, which is what keeps the builders testable in the node project and
 * keeps the leak guard satisfied.
 */
import type { EChartsOption } from 'echarts';
import { chartTokens, type ChartMedia, type ChartMode } from '@/tokens/chart';
import type { ChartConfig, SeriesConfig } from '../types';

/**
 * Categorical slots are assigned in fixed order and never generated. Past the
 * last slot we wrap rather than invent a hue: a chart with more series than
 * slots should fold the tail into "Other" or facet instead.
 */
export function seriesColor(index: number, series: SeriesConfig, mode: ChartMode): string {
  if (series.color) return series.color;
  const slots = chartTokens[mode].series;
  return slots[index % slots.length];
}

/** Whether a legend is drawn, and where — needed by both the legend and the grid. */
function legendPlacement(config: ChartConfig): {
  show: boolean;
  position: 'top' | 'bottom' | 'left' | 'right';
} {
  const visible = config.series.filter((s) => !s.hidden);
  // A single series is already named by the title; a legend box adds noise
  // without adding identity.
  return {
    show: config.legend?.show ?? visible.length > 1,
    position: config.legend?.position ?? 'bottom',
  };
}

export function resolveLegend(config: ChartConfig, mode: ChartMode): EChartsOption['legend'] {
  const { show, position } = legendPlacement(config);
  const tokens = chartTokens[mode];

  return {
    show,
    textStyle: { color: tokens.ink.label },
    ...(position === 'left' || position === 'right'
      ? { orient: 'vertical' as const, [position]: 0 }
      : { orient: 'horizontal' as const, [position]: 0 }),
  };
}

/** Space reserved for the legend on the edge it occupies, in pixels. */
const LEGEND_INSET = 32;

/**
 * Plot insets.
 *
 * `containLabel` reserves room for axis labels but knows nothing about the
 * legend, so without an explicit inset a bottom legend lands on top of the
 * category labels. Each edge therefore adds room only when the legend actually
 * sits there.
 */
export function resolveGrid(config: ChartConfig): EChartsOption['grid'] {
  const { show, position } = legendPlacement(config);
  const legendRoom = (edge: typeof position): number => (show && position === edge ? LEGEND_INSET : 0);

  return {
    left: 8 + legendRoom('left'),
    right: 16 + legendRoom('right'),
    top: (config.title ? 44 : 16) + legendRoom('top'),
    bottom: 8 + legendRoom('bottom'),
    containLabel: true,
  };
}

export function baseOption(
  config: ChartConfig,
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  const tokens = chartTokens[mode];
  const interactive = media === 'screen';
  const partToWhole = config.chartType === 'pie' || config.chartType === 'donut';

  return {
    backgroundColor: tokens.surface,
    color: [...tokens.series],
    animation: interactive,
    textStyle: { color: tokens.ink.label, fontSize: 12 },
    title: config.title
      ? {
          text: config.title,
          subtext: config.subtitle,
          left: 0,
          textStyle: { color: tokens.ink.label, fontSize: 14, fontWeight: 600 },
          subtextStyle: { color: tokens.ink.label, fontSize: 12 },
        }
      : undefined,
    grid: resolveGrid(config),
    legend: resolveLegend(config, mode),
    tooltip: interactive
      ? {
          // A pie has no axis to trigger against.
          trigger: partToWhole ? 'item' : 'axis',
          backgroundColor: tokens.ink.tooltipBg,
          borderColor: tokens.ink.tooltipBorder,
          borderWidth: 1,
          textStyle: { color: tokens.ink.tooltipText, fontSize: 12 },
        }
      : { show: false },
    aria: {
      // The generated description is the table-equivalent for screen readers.
      // It costs nothing visually, so it is always on.
      enabled: true,
      // Decals are the opposite trade: a real visual change to every mark, so
      // they are opt-in via `config.decals` rather than a default. Note this
      // means hue is the sole identity channel unless a consumer opts in — see
      // the `decals` docs in `types.ts`.
      decal: { show: config.decals === true },
    },
  };
}
